<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const assistantId = computed(() => Number(route.params.assistantId));
const isFetching = ref(false);
const orders = ref([]);
const page = ref(1);
const totalCount = ref(0);
const itemsPerPage = 25;

const isEmpty = computed(
  () => !isFetching.value && orders.value.length === 0 && page.value === 1
);

const statusLabel = status =>
  t(`WEB_ORDER.STATUS.${String(status).toUpperCase().replaceAll('-', '_')}`);

const conversationTo = order => {
  if (!order.display_id) return null;
  return {
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: order.display_id,
    },
  };
};

const openOrder = order => {
  router.push({
    name: 'web_order_order_detail',
    params: { orderId: order.id },
  });
};

const fetchOrders = async () => {
  isFetching.value = true;
  try {
    const { data } = await CaptainAssistantAPI.getOrders({
      assistantId: assistantId.value,
      page: page.value,
    });
    orders.value = Array.isArray(data?.payload) ? data.payload : [];
    totalCount.value = Number(data?.meta?.total_count || 0);
  } catch {
    orders.value = [];
    totalCount.value = 0;
  } finally {
    isFetching.value = false;
  }
};

const onPageChange = nextPage => {
  page.value = nextPage;
};

watch(
  [assistantId, page],
  () => {
    fetchOrders();
  },
  { immediate: true }
);

watch(assistantId, () => {
  page.value = 1;
});
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.ORDERS.HEADER')"
    :is-fetching="isFetching"
    :is-empty="isEmpty"
    :show-pagination-footer="totalCount > itemsPerPage"
    :current-page="page"
    :total-count="totalCount"
    :items-per-page="itemsPerPage"
    :show-know-more="false"
    class="[&>header>div]:max-w-[80rem] [&>main>div]:max-w-[80rem]"
    @update:current-page="onPageChange"
  >
    <template #emptyState>
      <EmptyStateLayout
        :title="t('CAPTAIN.ORDERS.EMPTY_STATE.TITLE')"
        :subtitle="t('CAPTAIN.ORDERS.EMPTY_STATE.SUBTITLE')"
      />
    </template>
    <template #body>
      <div class="overflow-x-auto rounded-xl border border-n-weak">
        <table class="w-full text-sm text-left">
          <thead class="text-n-slate-11 bg-n-alpha-2">
            <tr>
              <th class="px-4 py-3 font-medium">
                {{ t('CAPTAIN.ORDERS.COLUMNS.INVOICE') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAPTAIN.ORDERS.COLUMNS.STATUS') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAPTAIN.ORDERS.COLUMNS.CUSTOMER') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAPTAIN.ORDERS.COLUMNS.PRODUCTS') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAPTAIN.ORDERS.COLUMNS.INBOX') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAPTAIN.ORDERS.COLUMNS.CONVERSATION') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAPTAIN.ORDERS.COLUMNS.CREATED') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="order in orders"
              :key="order.id"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="openOrder(order)"
            >
              <td class="px-4 py-3 font-medium text-n-slate-12">
                {{ order.invoice_number || t('CAPTAIN.ORDERS.EMPTY_VALUE') }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ statusLabel(order.status) }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                <div>
                  {{ order.customer_name || t('CAPTAIN.ORDERS.EMPTY_VALUE') }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ order.customer_phone }}
                </div>
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ order.product_summary || t('CAPTAIN.ORDERS.EMPTY_VALUE') }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ order.inbox_name || t('CAPTAIN.ORDERS.EMPTY_VALUE') }}
              </td>
              <td class="px-4 py-3" @click.stop>
                <router-link
                  v-if="conversationTo(order)"
                  :to="conversationTo(order)"
                  class="text-n-blue-11 hover:underline"
                >
                  {{ t('CAPTAIN.ORDERS.OPEN_CONVERSATION') }}
                </router-link>
                <span v-else class="text-n-slate-11">{{
                  t('CAPTAIN.ORDERS.EMPTY_VALUE')
                }}</span>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{
                  order.created_at
                    ? new Date(order.created_at).toLocaleString()
                    : t('CAPTAIN.ORDERS.EMPTY_VALUE')
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </PageLayout>
</template>
